'use client';

import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import { Color, type Material, MathUtils, type Mesh, Vector2 } from 'three';
import { uniform } from 'three/tsl';
import { AdditiveBlending, DoubleSide, MeshBasicNodeMaterial } from 'three/webgpu';
import { createDotGridColorNode } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/backdrop/dot-grid-ground/dotGridShader';
import { type DotGridGroundProps } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/backdrop/dot-grid-ground/types';
import { NEAR_ZERO_3 } from '@/app/lcv/components/sections/spartan/shared/webgpu/constants';
export const DotGridGround = ({
  children,
  replayKey,
  pitch,
  rollDeg,
  horizon,
  horizonFeather,
  panX,
  panY,
  projectionScale,
  segments = 1,
  gridCount,
  dotRadius,
  dotSoftness,
  markEvery,
  markArmLength,
  markArmThickness,
  markBoost,
  gridCompression,
  fadeStart,
  fadeEnd,
  brightness,
  dotCeiling,
  opacity,
  origin,
  flashStrength,
  flashDecay,
  idleEnabled = true,
  idleGlow,
  idleFrequency,
  idleSpeed,
  idleFadeInSeconds,
  idleNoiseScale,
  idleNoiseSpeed,
  idleWarp,
  idleNoiseMix,
  idleHeight,
  idleFade,
  idleTroughFade,
  revealSpeed,
  revealSoftness,
  turnOnStrength,
  turnOnDecay,
  colorLow,
  colorMid,
  colorHigh,
  visibilityRef,
  warmUp = false,
  heatRef,
  colorLowTo,
  colorMidTo,
  colorHighTo,
  isAnimating = true
}: DotGridGroundProps) => {
  /* eslint-disable react-hooks/exhaustive-deps */
  // Starts at zero and simply runs. The original parked this behind a
  // negative value until an engine struck the floor; with no engine, the
  // sweep is the mount itself.
  const elapsedUniform = useMemo(() => uniform(0), []);
  const originUniform = useMemo(() => uniform(new Vector2(...origin)), []);

  // -- THE PROJECTION'S UNIFORMS.
  const pitchUniform = useMemo(() => uniform(pitch), []);
  const rollUniform = useMemo(() => uniform(MathUtils.degToRad(rollDeg)), []);
  const horizonUniform = useMemo(() => uniform(horizon), []);
  const horizonFeatherUniform = useMemo(() => uniform(horizonFeather), []);
  const panXUniform = useMemo(() => uniform(panX), []);
  const panYUniform = useMemo(() => uniform(panY), []);
  const projectionScaleUniform = useMemo(() => uniform(projectionScale), []);
  // Written per frame from the viewport rather than taken as a prop, so a
  // cell stays square when the window changes shape. The quad is exactly
  // the viewport, so without this the pattern would stretch with it.
  const aspectUniform = useMemo(() => uniform(1), []);
  const flashStrengthUniform = useMemo(() => uniform(flashStrength), []);
  const flashDecayUniform = useMemo(() => uniform(flashDecay), []);
  const flashDelayUniform = useMemo(() => uniform(0), []);
  const idleGlowUniform = useMemo(() => uniform(idleGlow), []);
  const idleFrequencyUniform = useMemo(() => uniform(idleFrequency), []);
  const idleSpeedUniform = useMemo(() => uniform(idleSpeed), []);
  const idleFadeInUniform = useMemo(() => uniform(idleFadeInSeconds), []);
  const idleNoiseScaleUniform = useMemo(() => uniform(idleNoiseScale), []);
  const idleNoiseSpeedUniform = useMemo(() => uniform(idleNoiseSpeed), []);
  const idleWarpUniform = useMemo(() => uniform(idleWarp), []);
  const idleHeightUniform = useMemo(() => uniform(idleHeight), []);
  const idleNoiseMixUniform = useMemo(() => uniform(idleNoiseMix), []);
  const idleFadeUniform = useMemo(() => uniform(idleFade), []);
  const idleTroughFadeUniform = useMemo(() => uniform(idleTroughFade), []);
  const revealSpeedUniform = useMemo(() => uniform(revealSpeed), []);
  const revealSoftnessUniform = useMemo(() => uniform(revealSoftness), []);
  const turnOnStrengthUniform = useMemo(() => uniform(turnOnStrength), []);
  const turnOnDecayUniform = useMemo(() => uniform(turnOnDecay), []);
  const gridCountUniform = useMemo(() => uniform(gridCount), []);
  const dotRadiusUniform = useMemo(() => uniform(dotRadius), []);
  const dotSoftnessUniform = useMemo(() => uniform(dotSoftness), []);
  const markEveryUniform = useMemo(() => uniform(markEvery), []);
  const markArmLengthUniform = useMemo(() => uniform(markArmLength), []);
  const markArmThicknessUniform = useMemo(() => uniform(markArmThickness), []);
  const markBoostUniform = useMemo(() => uniform(markBoost), []);
  const gridCompressionUniform = useMemo(() => uniform(gridCompression), []);
  const fadeStartUniform = useMemo(() => uniform(fadeStart), []);
  const fadeEndUniform = useMemo(() => uniform(fadeEnd), []);
  const brightnessUniform = useMemo(() => uniform(brightness), []);
  const dotCeilingUniform = useMemo(() => uniform(dotCeiling), []);
  const opacityUniform = useMemo(() => uniform(opacity), []);
  const colorLowUniform = useMemo(() => uniform(new Color(colorLow)), []);
  const colorMidUniform = useMemo(() => uniform(new Color(colorMid)), []);
  const colorHighUniform = useMemo(() => uniform(new Color(colorHigh)), []);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Unconditional, so every dial is live without any uniform being
  // rebuilt -- rebuilding one would detach it from the compiled material.
  useEffect(() => {
    /* eslint-disable react-hooks/immutability */
    originUniform.value.set(origin[0], origin[1]);
    pitchUniform.value = pitch;
    rollUniform.value = MathUtils.degToRad(rollDeg);
    horizonUniform.value = horizon;
    horizonFeatherUniform.value = horizonFeather;
    panXUniform.value = panX;
    panYUniform.value = panY;
    projectionScaleUniform.value = projectionScale;
    flashStrengthUniform.value = flashStrength;
    flashDecayUniform.value = flashDecay;
    idleFrequencyUniform.value = idleFrequency;
    idleSpeedUniform.value = idleSpeed;
    idleFadeInUniform.value = idleFadeInSeconds;
    idleNoiseScaleUniform.value = idleNoiseScale;
    idleNoiseSpeedUniform.value = idleNoiseSpeed;
    idleWarpUniform.value = idleWarp;
    idleHeightUniform.value = idleHeight;
    idleNoiseMixUniform.value = idleNoiseMix;
    idleFadeUniform.value = idleFade;
    idleTroughFadeUniform.value = idleTroughFade;
    revealSpeedUniform.value = revealSpeed;
    revealSoftnessUniform.value = revealSoftness;
    turnOnStrengthUniform.value = turnOnStrength;
    turnOnDecayUniform.value = turnOnDecay;
    gridCountUniform.value = gridCount;
    dotRadiusUniform.value = dotRadius;
    dotSoftnessUniform.value = dotSoftness;
    markEveryUniform.value = markEvery;
    markArmLengthUniform.value = markArmLength;
    markArmThicknessUniform.value = markArmThickness;
    markBoostUniform.value = markBoost;
    gridCompressionUniform.value = gridCompression;
    fadeStartUniform.value = fadeStart;
    idleGlowUniform.value = idleGlow;
    dotCeilingUniform.value = dotCeiling;
    /* eslint-enable react-hooks/immutability */
  });
  const elapsedWriteRef = useRef(elapsedUniform);
  const meshRef = useRef<Mesh>(null);
  const colorLowWriteRef = useRef(colorLowUniform);
  const colorMidWriteRef = useRef(colorMidUniform);
  const colorHighWriteRef = useRef(colorHighUniform);
  const opacityWriteRef = useRef(opacityUniform);
  const fadeEndWriteRef = useRef(fadeEndUniform);

  // Both ends of each stop, kept as Color objects so the lerp allocates
  // nothing per frame. The `To` values fall back to the `From` ones, so a
  // caller that sets no heat palette sees no change at all.
  const lowFrom = useMemo(() => new Color(colorLow), [colorLow]);
  const midFrom = useMemo(() => new Color(colorMid), [colorMid]);
  const highFrom = useMemo(() => new Color(colorHigh), [colorHigh]);
  const lowTo = useMemo(() => new Color(colorLowTo ?? colorLow), [colorLowTo, colorLow]);
  const midTo = useMemo(() => new Color(colorMidTo ?? colorMid), [colorMidTo, colorMid]);
  const highTo = useMemo(() => new Color(colorHighTo ?? colorHigh), [colorHighTo, colorHigh]);

  // A new `replayKey` rewinds the clock, which re-runs the sweep.
  useEffect(() => {
    elapsedWriteRef.current.value = 0;
  }, [replayKey]);

  // World width and height at z = 0 for the active camera. Drives both the
  // quad's size and the shader's aspect correction, so the two can never
  // disagree about how wide the screen is.
  const viewport = useThree(state => state.viewport);
  const aspectWriteRef = useRef(aspectUniform);
  useEffect(() => {
    aspectWriteRef.current.value = viewport.width / Math.max(viewport.height, NEAR_ZERO_3);
  }, [viewport.width, viewport.height]);
  useFrame((_state, delta) => {
    if (isAnimating) {
      elapsedWriteRef.current.value += delta;
    }
    const effectiveOpacity = opacity * (visibilityRef ? visibilityRef.value : 1);
    opacityWriteRef.current.value = effectiveOpacity;
    fadeEndWriteRef.current.value = fadeEnd;

    // THE HEAT PALETTE. Recomputed from the `From` stops each frame rather
    // than nudged, so it lands exactly on the `To` set and stays there
    // instead of creeping past it.
    const heat = heatRef ? heatRef.value : 0;
    colorLowWriteRef.current.value.copy(lowFrom).lerp(lowTo, heat);
    colorMidWriteRef.current.value.copy(midFrom).lerp(midTo, heat);
    colorHighWriteRef.current.value.copy(highFrom).lerp(highTo, heat);
    const mesh = meshRef.current;
    if (mesh) {
      // STOPS RENDERING WHEN FADED OUT, which is not the same as being
      // invisible. A transparent material at alpha 0 is still submitted
      // and its fragment shader still runs on every pixel it covers --
      // and this one covers a very large plane, running multi-octave fbm
      // per pixel for the dots and the idle wave.
      //
      // THE GATE IS ON THE MATERIAL, NOT THE OBJECT, and that distinction
      // is load-bearing: `object.visible = false` culls the whole
      // SUBTREE, and the PowerRing is mounted as a child of this mesh --
      // see `children` in the render below. Gating the object would take
      // the ring down with the floor. Hiding the material skips this
      // plane's own draw -- the entire point of the gate -- and leaves
      // anything mounted on it free to render on its own terms.
      const groundMaterial = mesh.material as Material;
      groundMaterial.visible = warmUp || effectiveOpacity > NEAR_ZERO_3;
    }
  });
  const material = useMemo(() => {
    const ripple = {
      elapsed: elapsedUniform,
      impact: originUniform,
      flashStrength: flashStrengthUniform,
      flashDecay: flashDecayUniform,
      flashDelay: flashDelayUniform,
      idleGlow: idleGlowUniform,
      idleFrequency: idleFrequencyUniform,
      idleSpeed: idleSpeedUniform,
      idleFadeInSeconds: idleFadeInUniform,
      idleNoiseScale: idleNoiseScaleUniform,
      idleNoiseSpeed: idleNoiseSpeedUniform,
      idleWarp: idleWarpUniform,
      idleHeight: idleHeightUniform,
      idleNoiseMix: idleNoiseMixUniform,
      idleFade: idleFadeUniform,
      idleTroughFade: idleTroughFadeUniform,
      revealSpeed: revealSpeedUniform,
      revealSoftness: revealSoftnessUniform,
      turnOnStrength: turnOnStrengthUniform,
      turnOnDecay: turnOnDecayUniform
    };
    const look = {
      gridCount: gridCountUniform,
      dotRadius: dotRadiusUniform,
      dotSoftness: dotSoftnessUniform,
      markEvery: markEveryUniform,
      markArmLength: markArmLengthUniform,
      markArmThickness: markArmThicknessUniform,
      markBoost: markBoostUniform,
      gridCompression: gridCompressionUniform,
      fadeStart: fadeStartUniform,
      fadeEnd: fadeEndUniform,
      brightness: brightnessUniform,
      dotCeiling: dotCeilingUniform,
      colorLow: colorLowUniform,
      colorMid: colorMidUniform,
      colorHigh: colorHighUniform,
      opacity: opacityUniform
    };
    const projection = {
      pitch: pitchUniform,
      rollRad: rollUniform,
      horizon: horizonUniform,
      horizonFeather: horizonFeatherUniform,
      originX: panXUniform,
      originY: panYUniform,
      scale: projectionScaleUniform,
      aspect: aspectUniform
    };
    const groundMaterial = new MeshBasicNodeMaterial();
    const {
      colorNode,
      opacityNode
    } = createDotGridColorNode(ripple, look, projection, idleEnabled);

    // NO positionNode. Nothing displaces vertices any more -- see the note
    // where createDotGridPositionNode was removed from dotGridShader.
    groundMaterial.colorNode = colorNode;
    groundMaterial.opacityNode = opacityNode;
    groundMaterial.transparent = true;
    groundMaterial.depthWrite = false;
    groundMaterial.blending = AdditiveBlending;
    groundMaterial.side = DoubleSide;
    return groundMaterial;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idleEnabled]);
  useEffect(() => () => {
    material.dispose();
  }, [material]);
  return (
    // EXACTLY THE VIEWPORT, facing the camera. `viewport` is R3F's world
    // width and height at z = 0 for the active camera, so this is a quad
    // that fills the frame and no more -- full-bleed by construction, at
    // every aspect, with no fit to solve and nothing shaded off-screen.
    //
    // The rake the old plane got from being rotated is now applied to the
    // coordinate inside the shader instead. See groundProjection.
    <mesh ref={meshRef} material={material}>
      <planeGeometry args={[viewport.width, viewport.height, segments, segments]} />
      {/* ANYTHING THAT HAS TO SIT ON THIS FLOOR goes here -- the
          PowerRing above all. */}
      {children}
    </mesh>
  );
};